"use client";

import { useMemo, useEffect, useCallback, useState, useRef, memo } from "react";
import { cn } from "@/lib/utils";
import { ArrowLeftCircle, ArrowRightCircle, ChevronRight } from "lucide-react";
import { useSearchParams } from "next/navigation";
import useUrlQuery from "@/hooks/useUrlQuery";
import * as NProgress from "nprogress";
import { useScopedI18n } from "@/messages/client";
import InputNumber from "../Form/InputNumber";

interface PaginationProps {
  className?: string;
  total: number;
  pageSize?: number;
  current: number;
  onChange?: (current: number) => void;
}

function Pagination({
  className,
  total,
  pageSize = 10,
  current,
  onChange,
}: PaginationProps) {
  const t = useScopedI18n("分页");

  const [jumpPage, setJumpPage] = useState<number>();

  const [pageRenderArray, setPageRenderArray] = useState<Array<number>>([]);

  const pageChange = useCallback(
    (page: number) => {
      if (page > 1) {
        onChange?.(page);
      } else {
        onChange?.(1);
      }
    },
    [onChange]
  );

  const totalPage = useMemo(() => {
    return Math.ceil(total / pageSize);
  }, [total, pageSize]);

  //点击改页码
  const changePage = useCallback(
    (pageNum: number) => {
      //小型分页器
      if (totalPage <= 6) {
        pageChange(pageNum);

        return;
      }

      let newPageRenderArray: number[] = [];

      if (pageNum > 4 && pageNum <= totalPage - 4) {
        newPageRenderArray = [
          pageNum - 2,
          pageNum - 1,
          pageNum,
          pageNum + 1,
          pageNum + 2,
        ];
      }
      //页码为1的情况
      if (pageNum <= 4) {
        newPageRenderArray = [1, 2, 3, 4, 5];
      }
      //页码到倒数第四页内的情况
      if (pageNum > totalPage - 4) {
        newPageRenderArray = [
          totalPage - 4,
          totalPage - 3,
          totalPage - 2,
          totalPage - 1,
          totalPage,
        ];
      }

      setPageRenderArray(newPageRenderArray);
      pageChange(pageNum);
    },
    [pageChange, totalPage]
  );

  const handlePrevious = useCallback(() => {
    if (current && current > 1) {
      changePage(current - 1);
    }
  }, [current, changePage]);

  const handleNext = useCallback(() => {
    if (current && current < totalPage) {
      changePage(current + 1);
    }
  }, [current, totalPage, changePage]);

  useEffect(() => {
    if (totalPage >= 7) {
      setPageRenderArray([1, 2, 3, 4, 5]);
    } else {
      if (totalPage >= 2) {
        const array = new Array(totalPage as number).fill(0);
        array.forEach((_, index) => {
          array[index] = index + 1;
        });
        setPageRenderArray(array);
      } else {
        setPageRenderArray([]);
      }
    }
  }, [pageSize, totalPage]);

  if (total === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-y-3 md:flex-row",
        className
      )}>
      <div className="flex select-none justify-center">
        <span
          className={cn(
            " flex h-8 w-8 items-center justify-center text-gray transition-all duration-300",
            {
              "cursor-pointer hover:text-secondary": current > 1,
              "cursor-not-allowed opacity-30": current === 1,
            }
          )}
          onClick={handlePrevious}>
          <ArrowLeftCircle />
        </span>
        {/** 第1页始终显示，如果分页器与第一页中间有其他页（即当前页>=5） 显示省略号 */}
        {pageRenderArray.length > 0 && !pageRenderArray.includes(1) && (
          <>
            <span
              className="mx-1 h-8 w-8 cursor-pointer text-center text-sm leading-8 text-gray hover:text-secondary"
              onClick={() => changePage(1)}>
              1
            </span>
            {current >= 5 && (
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center text-gray"
                )}>
                ···
              </span>
            )}
          </>
        )}

        {pageRenderArray.length ? (
          pageRenderArray.map((i) => (
            <span
              key={i}
              className={cn("mx-1 h-8 w-8 text-center text-sm leading-8", {
                "cursor-default text-primary": current === i,
                "cursor-pointer text-gray hover:text-secondary": current !== i,
              })}
              onClick={() => changePage(i)}>
              {i}
            </span>
          ))
        ) : (
          <span
            className={cn(
              "mx-1 h-8 w-8 cursor-default text-center text-sm leading-8 text-gray"
            )}>
            1
          </span>
        )}
        {/** 末页始终显示，如果分页器与末页中间有其他页（即当前页<末页-3） 显示省略号 */}
        {pageRenderArray.length > 0 && !pageRenderArray.includes(totalPage) && (
          <>
            {current < totalPage - 3 && (
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center text-gray"
                )}>
                ···
              </span>
            )}
            <span
              className="mx-1 h-8 w-8 cursor-pointer text-center text-sm leading-8 text-gray hover:text-secondary"
              onClick={() => changePage(totalPage)}>
              {totalPage}
            </span>
          </>
        )}
        <span
          className={cn(
            "flex h-8 w-8 items-center justify-center text-gray transition-all duration-300",
            {
              "cursor-pointer hover:text-secondary": current < totalPage,
              "cursor-not-allowed opacity-30": current === totalPage,
            }
          )}
          onClick={handleNext}>
          <ArrowRightCircle />
        </span>
      </div>
      <div className="ml-6 flex items-center text-gray">
        {t("前往第")}
        <InputNumber
          className="mx-2 h-6 w-12"
          field={{ value: jumpPage, onChange: setJumpPage }}
          min={1}
          max={totalPage}
          stepper={false}
        />
        {t("页")}
        <span
          className={cn(
            "ml-5 flex cursor-pointer items-center hover:text-primary",
            {
              "pointer-events-none": !jumpPage || Number(jumpPage) === current,
            }
          )}
          onClick={() => changePage(Number(jumpPage || 1))}>
          {t("跳转")}

          <ChevronRight
            className={cn({
              "text-primary": jumpPage && Number(jumpPage) !== current,
            })}
          />
        </span>
      </div>
    </div>
  );
}

export default memo(Pagination);
